import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { Check, ChevronDown, Plus, Settings, X } from "lucide-react"
import { useProfile } from "~/hooks/useProfile"
import { hasEntitlement, ENTITLEMENTS } from "~/utils/entitlements"
import { activateDog } from "~/api/dogs"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"

interface DogSelectorProps {
  onDogChanged?: () => void
  /** When set (e.g. pack mode on Ask Dynamo), replaces the active dog name on the trigger. */
  triggerLabel?: string
}

function DogAvatar({
  name,
  archetypeIcon,
  portraitUrl,
  sizeClass = "h-9 w-9",
}: {
  name: string
  archetypeIcon?: string | null
  portraitUrl?: string | null
  sizeClass?: string
}) {
  const initial = (name.trim().charAt(0) || "?").toUpperCase()
  if (portraitUrl) {
    return (
      <img
        src={portraitUrl}
        alt=""
        className={`${sizeClass} shrink-0 rounded-full object-cover ring-1 ring-[var(--color-divider)]`}
      />
    )
  }
  if (archetypeIcon) {
    const archetypeSrc = heroPortraitUrl(archetypeIcon, null)
    if (archetypeSrc) {
      return (
        <img
          src={archetypeSrc}
          alt=""
          className={`${sizeClass} shrink-0 rounded-full object-cover ring-1 ring-[var(--color-divider)]`}
        />
      )
    }
  }
  return (
    <span
      aria-hidden
      className={`inline-flex ${sizeClass} shrink-0 items-center justify-center rounded-full bg-[var(--color-ask-dynamo-surface)] text-sm font-semibold text-[var(--color-ask-dynamo)] ring-1 ring-[var(--color-ask-dynamo)]/20`}
    >
      {initial}
    </span>
  )
}

export function DogSelector({ onDogChanged, triggerLabel }: DogSelectorProps) {
  const navigate = useNavigate()
  const { profile, refetch } = useProfile()
  const [isOpen, setIsOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const sheetRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false)
    }

    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [isOpen])

  if (!profile?.dog_profile) return null

  const dogs = (profile.dogs || []).filter((dog) => !dog.plus_locked)
  const activeDog = profile.dog_profile
  const isPlus = hasEntitlement(profile.user.entitlements, ENTITLEMENTS.multipleDogs)
  const activePortrait = heroPortraitUrl(activeDog.archetype_icon, activeDog.portrait)
  const triggerName = triggerLabel ?? activeDog.name

  const handleActivate = async (dogId: number) => {
    if (dogId === profile.active_dog_profile_id) {
      setIsOpen(false)
      return
    }

    setSwitching(true)
    try {
      await activateDog(dogId)
      await refetch()
      setIsOpen(false)
      onDogChanged?.()
    } catch (error) {
      console.error("Failed to switch dog:", error)
    } finally {
      setSwitching(false)
    }
  }

  const handleAddDog = () => {
    setIsOpen(false)
    navigate("/pack/add")
  }

  const handleManage = () => {
    setIsOpen(false)
    navigate("/pack/manage")
  }

  const handleLearnMore = () => {
    setIsOpen(false)
    navigate("/plus")
  }

  const closeSheet = () => setIsOpen(false)

  const overlay =
    isOpen &&
    createPortal(
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
        onClick={closeSheet}
        role="presentation"
        data-testid="dog-selector-overlay"
      >
        <div
          ref={sheetRef}
          role="dialog"
          aria-modal="true"
          aria-label={isPlus ? "Your Pack" : "Your Dog"}
          data-testid="dog-selector-sheet"
          className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-[var(--color-surface-card)] shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between border-b border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              {isPlus ? "Your Pack" : "Your Dog"}
            </h2>
            <button
              type="button"
              onClick={closeSheet}
              aria-label="Close dog selector"
              className="rounded-lg p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
            >
              <X size={20} aria-hidden />
            </button>
          </div>

          <div
            ref={listRef}
            className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-4"
            data-testid="dog-selector-list"
            onTouchMove={(event) => event.stopPropagation()}
          >
            {dogs.map((dog) => {
              const isActive = dog.id === profile.active_dog_profile_id
              const portrait = heroPortraitUrl(dog.archetype_icon, dog.portrait)
              return (
                <button
                  key={dog.id}
                  type="button"
                  onClick={() => void handleActivate(dog.id)}
                  disabled={switching}
                  aria-current={isActive ? "true" : undefined}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl border p-4 text-left transition ${
                    isActive
                      ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)]/10"
                      : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)]"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <DogAvatar
                      name={dog.name}
                      archetypeIcon={dog.archetype_icon}
                      portraitUrl={portrait}
                    />
                    <span>
                      <span className="block font-medium text-[var(--color-text-primary)]">
                        {dog.name}
                      </span>
                      <span className="block text-sm text-[var(--color-text-secondary)]">
                        {dog.archetype_label}
                      </span>
                    </span>
                  </span>
                  {isActive && (
                    <Check
                      size={18}
                      className="shrink-0 text-[var(--color-brand-primary)]"
                      aria-label="Selected"
                    />
                  )}
                </button>
              )
            })}
          </div>

          {isPlus ? (
            <div className="sticky bottom-0 shrink-0 space-y-2 border-t border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-4">
              <button
                type="button"
                onClick={handleAddDog}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
              >
                <Plus size={16} aria-hidden />
                Add Another Dog
              </button>
              <button
                type="button"
                onClick={handleManage}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-4 py-3 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]"
              >
                <Settings size={16} aria-hidden />
                Manage Dogs
              </button>
            </div>
          ) : (
            <div className="sticky bottom-0 shrink-0 space-y-2 border-t border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-4">
              <button
                type="button"
                onClick={handleLearnMore}
                className="w-full rounded-xl bg-[var(--color-brand-primary)] px-4 py-3 text-center text-sm font-medium text-white hover:opacity-90"
              >
                Add Another Dog with Plus
              </button>
              <button
                type="button"
                onClick={handleLearnMore}
                className="w-full text-center text-sm font-medium text-[var(--color-brand-primary)] underline"
              >
                Learn more about Plus
              </button>
            </div>
          )}
        </div>
      </div>,
      document.body,
    )

  return (
    <>
      <button
        type="button"
        data-testid="dog-selector-trigger"
        onClick={() => setIsOpen(true)}
        className="flex w-full max-w-sm items-center gap-3 rounded-2xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] px-3 py-2.5 text-left shadow-sm hover:bg-[var(--color-surface-hover)]"
        disabled={switching}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={triggerLabel ? `Switch dog (${triggerLabel})` : "Choose dog from your pack"}
      >
        {!triggerLabel && (
          <DogAvatar
            name={activeDog.name}
            archetypeIcon={activeDog.archetype_icon}
            portraitUrl={activePortrait}
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-base font-bold text-[var(--color-text-primary)]">
            {triggerName}
          </span>
          <span className="block text-xs font-medium text-[var(--color-text-secondary)]">
            Your pack
          </span>
        </span>
        <ChevronDown
          size={20}
          className="shrink-0 text-[var(--color-ask-dynamo)]"
          aria-hidden
        />
      </button>

      {overlay}
    </>
  )
}
